import { createValue } from '@interhyp/platform-value';
import { nanoid } from 'nanoid';
import { createContext, useContext, useMemo, useEffect, useRef, useCallback } from 'react';
import { jsx } from 'react/jsx-runtime';

const deserialize = data => {
  return JSON.parse(data);
};

const serialize = (value, nonce) => {
  return JSON.stringify({
    value,
    nonce
  });
};

const getOrInitStoredValue = (storage, storageKey, initialize) => {
  const value = storage.getItem(storageKey);
  if (value === null) {
    if (initialize) {
      const serializedValue = serialize(...initialize());
      storage.setItem(storageKey, serializedValue);
      return deserialize(serializedValue); // To make sure the initial return value is the same
    }
    return undefined;
  }
  return deserialize(value);
};

/**
 * Tries to return the requested storage provider or fails gracefully by returning undefined.
 */
const safeGetStorage = type => {
  try {
    if (typeof window !== 'undefined') {
      return type === 'session' ? sessionStorage : localStorage;
    }
  } catch (error) {
    /* eslint-disable no-console */
    if (error instanceof Error && error.name === 'SecurityError') {
      console.info(`Value storage is not available. Presumably third party cookies are not allowed:\n{error.stack}`);
    }
    console.error(error);
    /* eslint-enable no-console */
  }
  return undefined;
};

const storageContext = /*#__PURE__*/createContext(undefined);

function useStoragePrefix() {
  const context = useContext(storageContext);
  return context?.prefix;
}

function useStorageKey(valueDefinition) {
  const prefix = useStoragePrefix();
  return useMemo(() => {
    return [prefix, valueDefinition.namespace.id.description, valueDefinition.id.description].filter(Boolean).join(':');
  }, [prefix, valueDefinition.id.description, valueDefinition.namespace.id.description]);
}

function useStorageUpdateListener(handleStorageChange) {
  useEffect(() => {
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleStorageChange]);
}

function useMemoryOnly(currentValue) {
  const context = useContext(storageContext);
  return currentValue ?? context?.memoryOnly ?? false;
}

/**
 * This function will provide a state and set-state method where the value wil be saved in the local / session storage
 * @param storageConfig
 */
function createBrowserStorage(storageConfig) {
  const {
    useValue
  } = createValue(storageConfig);
  let doUseMemoryValue = false;
  const useBrowserStorageValue = opts => {
    const memoryOnly = useMemoryOnly(opts?.memoryOnly);
    const storageKey = useStorageKey(storageConfig);
    const nonce = useRef(undefined);

    // cache the storage provider access to print a possible error message only once.
    const storage = useMemo(() => {
      if (memoryOnly) {
        return undefined;
      }
      return safeGetStorage(storageConfig.type);
    }, [memoryOnly]);
    const [memoryValue, setMemoryValue] = useValue();

    // Get value from store
    const storedValue = useMemo(() => {
      return storage ? getOrInitStoredValue(storage, storageKey, () => [storageConfig.initialValue, nonce.current = nanoid()])?.value : undefined;
    }, [storage, storageKey]);

    // Init set value function
    const setStorageValue = useCallback(value => {
      doUseMemoryValue = true;
      setMemoryValue(value);
      if (memoryOnly || !storage) {
        return;
      }
      if (typeof value === 'undefined') {
        storage.removeItem(storageKey);
      } else {
        nonce.current = nanoid(); // generate and save a new nonce that uniquely identifies the value.
        storage.setItem(storageKey, serialize(value, nonce.current));
      }
    }, [setMemoryValue, memoryOnly, storage, storageKey]);

    // Init store update listener
    const handleStorageChange = useCallback(event => {
      if (event.key !== storageKey) {
        return;
      }
      if (typeof event.newValue === 'undefined' || event.newValue === null) {
        doUseMemoryValue = true;
        setMemoryValue(undefined);
      } else {
        try {
          const data = deserialize(event.newValue);
          // if the stored nonce of a value is the same as the latest cached one, then no
          // update must be propagated, e.g. the hook doesn't re-render.
          if (nonce.current === undefined || nonce.current !== data.nonce) {
            doUseMemoryValue = true;
            nonce.current = data.nonce;
            setMemoryValue(data.value);
          }
        } catch {
          doUseMemoryValue = true;
          setMemoryValue(undefined);
        }
      }
    }, [setMemoryValue, storageKey]);
    useStorageUpdateListener(handleStorageChange);

    // Check if memory or stored value should be used
    const returnValue = doUseMemoryValue ? memoryValue : storedValue;
    return useMemo(() => {
      return [returnValue, setStorageValue];
    }, [returnValue, setStorageValue]);
  };
  return {
    useValue: useBrowserStorageValue
  };
}

function createStorageValue(storageConfig) {
  if (storageConfig.type === 'local' || storageConfig.type === 'session') {
    return createBrowserStorage(storageConfig);
  }
  throw new Error(`Unknown storage type ${storageConfig.type}`);
}

function StorageProvider({
  children,
  prefix,
  memoryOnly
}) {
  const value = useMemo(() => {
    return {
      prefix,
      memoryOnly
    };
  }, [memoryOnly, prefix]);
  return /*#__PURE__*/jsx(storageContext.Provider, {
    value: value,
    children: children
  });
}

export { StorageProvider, createStorageValue };
//# sourceMappingURL=index.js.map
